import {
  CellErrorCode,
  ColumnName,
  FormState,
  ProcessCensusFileResponseStatus,
  ProcessedCellStatus,
  ValueOf,
} from "./types";

export const COLUMNS = {
  RELATIONSHIP: "relationship",
  OCCUPATION: "occupation",
  GENDER: "gender",
  ZIP: "zip",
  DATE_OF_BIRTH: "date of birth",
  EMPLOYMENT_TYPE: "employment type",
  EMPLOYMENT_STATUS: "employment status",
  ELIGIBILITY_CLASS: "eligibility class",
  ANNUAL_SALARY: "annual salary",
  STD_ELECTION: "std election",
  LTD_ELECTION: "ltd election",
  DENTAL_PLAN_COVERAGE: "dental plan coverage",
  DENTAL_PLAN_NAME: "dental plan name",
  VISION_PLAN_COVERAGE: "vision plan coverage",
  VISION_PLAN_NAME: "vision plan name",
  BASIC_LIFE_ADD_EE_COVERAGE: "basic life/ad&d ee coverage",
  VOLUNTARY_LIFE_EE_COVERAGE: "vol life ee coverage",
  VOLUNTARY_ADD_EE_COVERAGE: "vol ad&d ee coverage",
  BENEFIT: "benefit",
  PLAN_NAME: "plan name",
  COVERAGE_TIER: "coverage tier",
} as const;

export const COLUMNS_BY_FORMAT = {
  COMMON: [
    "relationship",
    "gender",
    "date of birth",
    "zip",
    "employment type",
    "employment status",
    "occupation",
    "eligibility class",
    "annual salary",
  ],
  COLUMN: [
    "dental plan coverage",
    "dental plan name",
    "vision plan coverage",
    "vision plan name",
    "std election",
    "ltd election",
    "basic life/ad&d ee coverage",
    "vol life ee coverage",
    "vol ad&d ee coverage",
  ],
  ROW: ["benefit", "plan name", "coverage tier"],
} satisfies Record<string, ValueOf<typeof COLUMNS>[]>;

export const GENDERS = {
  MALE: "Male",
  FEMALE: "Female",
  NON_BINARY: "Non Binary",
  NOT_DISCLOSED: "Not Disclosed",
} as const;

export const RELATIONSHIPS = {
  DOMESTIC_PARTNER: "Domestic Partner",
  EMPLOYEE: "Employee",
  SPOUSE: "Spouse",
  CHILD: "Child",
  LEGAL_DEPENDENT: "Legal Dependent",
} as const;

export const BENEFIT_COVERAGES = {
  STD: "short term disability",
  LTD: "long term disability",
  BASIC_LIFE: "basic life",
  VOLUNTARY_LIFE: "voluntary life",
};

export const TIERS_VALUES_MESSAGE =
  "'EE Only', 'EE', 'Employee Only', 'Employee', 'Single', 'EE + SP', 'EE+SP', 'EE + Spouse', 'Employee + Spouse', 'Employee + SP', 'Employee/Spouse', 'ESP', 'Employee and Spouse', 'ES', 'EE/SP', 'EE + DP', 'EE+DP', 'EE + Domestic Partner', 'Employee + Domestic Partner', 'Employee + DP', 'EDP', 'EE/DP', 'EE + CH', 'EE+CH', 'EE + Child', 'EE + Child(ren)', 'EE + Children', 'Employee + Child', 'Employee + Child(ren)', 'Employee + Children', 'Employee + CH', 'Employee/Child', 'ECH', 'Employee and Child', 'EC', 'EE/CH', 'EE + Fam', 'EE+Fam', 'EE + Family', 'EE+Family', 'Employee + Family', 'Employee + Fam', 'Employee + Spouse + 1 Child', 'Employee + Spouse + 2 Children', 'Employee/Family', 'FAM', 'Employee and Family', 'F', 'EE + 1', 'EE+1', 'EE + 2', 'EE+2', 'Spouse Only', 'Child Only', 'Spouse + Dependent', 'Spouse + Children', 'Not Disclosed', 'EO', 'EF', 'Family'";
export const COVERED_VALUES_MESSAGE = "'Covered', 'Included', 'X', 'Yes', 'Y'";
export const WAIVED_VALUES_MESSAGE =
  "an empty value or values: 'Waived', 'Not Covered', 'Waive', 'No', '0', 'N', 'WV', 'Waiver', 'W'";
export const BENEFIT_VALUES_MESSAGE =
  "'Dental', 'Vision', 'Short Term Disability', 'Short-Term Disability', 'STD', 'Long-Term Disability', 'LTD', 'Basic Life', 'Employer Provided Life', 'Employer Life', 'Company-Paid Life', 'Employer-Paid Life', 'Voluntary Life', 'Voluntary Employee Life', 'Employee-Paid Life', 'Life Vol EE', 'Vol Life', 'Voluntary Life and AD&D', 'Voluntary Life with AD&D', 'Voluntary Life w/ AD&D', 'Voluntary AD&D', 'AD&D Vol EE', 'Vol AD&D'";

export const HEADER_LOOKUP: { [key: string]: ColumnName } = {
  relationship: "relationship",
  gender: "gender",
  sex: "gender",
  "date of birth": COLUMNS.DATE_OF_BIRTH,
  dob: COLUMNS.DATE_OF_BIRTH,
  birthday: COLUMNS.DATE_OF_BIRTH,
  "birth date": COLUMNS.DATE_OF_BIRTH,
  zip: "zip",
  zipcode: "zip",
  "zip code": "zip",
  "postal code": "zip",
  "home zip": "zip",
  "home zip code": "zip",
  "(home) zip code": "zip",
  "employment type": COLUMNS.EMPLOYMENT_TYPE,
  "employee type": COLUMNS.EMPLOYMENT_TYPE,
  "work status (ft, pt)": COLUMNS.EMPLOYMENT_TYPE,
  "worker category": COLUMNS.EMPLOYMENT_TYPE,
  "worker category description": COLUMNS.EMPLOYMENT_TYPE,
  "employment status": COLUMNS.EMPLOYMENT_STATUS,
  "employee status description": COLUMNS.EMPLOYMENT_STATUS,
  status: COLUMNS.EMPLOYMENT_STATUS,
  "member status": COLUMNS.EMPLOYMENT_STATUS,
  "employee status": COLUMNS.EMPLOYMENT_STATUS,
  "active/cobra/retiree": COLUMNS.EMPLOYMENT_STATUS,
  "employment status (active, cobra, retiree, leave)":
    COLUMNS.EMPLOYMENT_STATUS,
  "position status": COLUMNS.EMPLOYMENT_STATUS,
  "employee position status": COLUMNS.EMPLOYMENT_STATUS,
  occupation: "occupation",
  "job title": "occupation",
  position: "occupation",
  title: "occupation",
  "eligibility class": COLUMNS.ELIGIBILITY_CLASS,
  "benefit class": COLUMNS.ELIGIBILITY_CLASS,
  class: COLUMNS.ELIGIBILITY_CLASS,
  "employee class": COLUMNS.ELIGIBILITY_CLASS,
  "employment class": COLUMNS.ELIGIBILITY_CLASS,
  "annual salary": COLUMNS.ANNUAL_SALARY,
  "annualized salary": COLUMNS.ANNUAL_SALARY,
  "annual base salary": COLUMNS.ANNUAL_SALARY,
  "annual salary amount": COLUMNS.ANNUAL_SALARY,
  "yearly salary": COLUMNS.ANNUAL_SALARY,
  "dental plan coverage": COLUMNS.DENTAL_PLAN_COVERAGE,
  "dental coverage": COLUMNS.DENTAL_PLAN_COVERAGE,
  "dental enrollment tier": COLUMNS.DENTAL_PLAN_COVERAGE,
  "dental enrollment status": COLUMNS.DENTAL_PLAN_COVERAGE,
  "dental coverage type": COLUMNS.DENTAL_PLAN_COVERAGE,
  "dental plan name": COLUMNS.DENTAL_PLAN_NAME,
  "dental plan election": COLUMNS.DENTAL_PLAN_NAME,
  "dental election": COLUMNS.DENTAL_PLAN_NAME,
  "dental plan (high vs. low)": COLUMNS.DENTAL_PLAN_NAME,
  "dental plan": COLUMNS.DENTAL_PLAN_NAME,
  "dental plan option": COLUMNS.DENTAL_PLAN_NAME,
  "dental enrollment plan": COLUMNS.DENTAL_PLAN_NAME,
  "vision plan coverage": COLUMNS.VISION_PLAN_COVERAGE,
  "vision coverage": COLUMNS.VISION_PLAN_COVERAGE,
  "vision enrollment tier": COLUMNS.VISION_PLAN_COVERAGE,
  "vision enrollment status": COLUMNS.VISION_PLAN_COVERAGE,
  "vision coverage type": COLUMNS.VISION_PLAN_COVERAGE,
  "vision plan name": COLUMNS.VISION_PLAN_NAME,
  "vision plan election": COLUMNS.VISION_PLAN_NAME,
  "vision election": COLUMNS.VISION_PLAN_NAME,
  "vision plan (high vs. low)": COLUMNS.VISION_PLAN_NAME,
  "vision plan": COLUMNS.VISION_PLAN_NAME,
  "vision enrollment plan": COLUMNS.VISION_PLAN_NAME,
  "basic life/ad&d ee coverage": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "basic life/ad&d ee": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "basic life/ad&d": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "basic life/ad&d coverage": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "basic life ee": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "basic life": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "basic life coverage": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "std election": COLUMNS.STD_ELECTION,
  "std coverage": COLUMNS.STD_ELECTION,
  "short term disability coverage": COLUMNS.STD_ELECTION,
  "short-term disability coverage": COLUMNS.STD_ELECTION,
  "ltd election": COLUMNS.LTD_ELECTION,
  "ltd coverage": COLUMNS.LTD_ELECTION,
  "long term disability coverage": COLUMNS.LTD_ELECTION,
  "long-term disability coverage": COLUMNS.LTD_ELECTION,
  "vol life ee coverage": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "vol life ee": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "voluntary life ee": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "ee vol life": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "voluntary life ee coverage": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "vol ad&d ee coverage": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "vol ad&d ee": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "voluntary ad&d ee": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "ee vol ad&d": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "voluntary ad&d ee coverage": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  benefit: COLUMNS.BENEFIT,
  "benefit type": COLUMNS.BENEFIT,
  "benefit plan type": COLUMNS.BENEFIT,
  "product description": COLUMNS.BENEFIT,
  "plan name": "plan name",
  "benefit plan name": "plan name",
  plan: "plan name",
  "coverage tier": COLUMNS.COVERAGE_TIER,
  tier: COLUMNS.COVERAGE_TIER,
  "coverage name": COLUMNS.COVERAGE_TIER,
  "coverage level": COLUMNS.COVERAGE_TIER,
};

export const PROCESSED_CELL_STATUS: Record<string, ProcessedCellStatus> = {
  VALID: "valid",
  INVALID_VALUE: "invalid-value",
};

export const CELL_ERROR_CODE: Record<string, CellErrorCode> = {
  MULTIPLE_VALUES: "multiple-values",
  UNMAPPED_LIFE: "unmapped-life",
  MULTIPLE_BENEFITS: "multiple-benefits",
};

export const FORM_STATE_FIELDS = {
  STATE: "state",
  ROW: "row",
  SELECTED_COVERAGES: "selectedCoverages",
  CENSUS_FILE: "censusFile",
  CENSUS_ERROR: "censusError",
  FILE_ERROR: "fileError",
  PROCESSED_DATA: "processedData",
  FULL_TIME_EMPLOYEES: "fullTimeEmployees",
  CENSUS_GROUP_CLASSES: "censusGroupClasses",
  CLASS_NAMES_DIFFER: "classNamesDiffer",
  COVERAGES_COLUMNS: "coveragesColumns",
} as const satisfies Record<string, keyof FormState>;

export const BENEFIT_LIFE = "life";

export const PROCESS_CENSUS_FILE_RESPONSE_STATUS = {
  NOT_VALID_FILE: "not-valid-file",
  MISSING_WORKSHEET: "missing-worksheet",
  MULTIPLE_WORKSHEETS: "multiple-worksheets",
  MISSING_HEADER_ROW: "missing-header-row",
  MISSING_DATA_ROW: "missing-data-row",
  FIRST_ROW_INVALID_RELATIONSHIP: "first-row-invalid-relationship",
  MISSING_COVERAGES: "missing-coverages",
  HEADER_ERROR: "header-error",
  DATA_ERROR: "data-error",
  MAPPING_ERROR: "mapping-error",
  ON_PROCESSED_ERROR: "on-processed-error",
  SUCCESS: "success",
} as const satisfies Record<string, ProcessCensusFileResponseStatus>;
